import { Chess } from "chess.js";

export interface ParsedMove {
  ply: number;
  san: string;
  uci: string;
  color: "white" | "black";
  fenBefore: string;
  fenAfter: string;
  moveNumber: number;
}

export interface ParsedGame {
  headers: Record<string, string>;
  moves: ParsedMove[];
  result: string;
  opening?: string;
  eco?: string;
  pgn: string;
}

const HEADER_TAG_PATTERN = /^\s*\[\s*([A-Za-z0-9_]+)\s+"((?:[^"\\]|\\.)*)"\s*\]\s*$/;
const RESULT_MARKER_PATTERN = /(?:^|\s)(1-0|0-1|1\/2-1\/2|\*)\s*$/;
const VALID_RESULTS = new Set(["1-0", "0-1", "1/2-1/2", "*"]);

function unescapeHeaderValue(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, "\\");
}

function isHeaderLine(line: string): boolean {
  return HEADER_TAG_PATTERN.test(line);
}

function extractFirstGame(pgn: string): string {
  const lines = pgn.split(/\r?\n/);
  let seenMovetext = false;
  let sawResultMarker = false;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    if (line.trim() === "") continue;

    if (isHeaderLine(line)) {
      if (seenMovetext || sawResultMarker) return lines.slice(0, index).join("\n");
      continue;
    }

    seenMovetext = true;
    if (sawResultMarker) return lines.slice(0, index).join("\n");
    if (RESULT_MARKER_PATTERN.test(line.trim())) sawResultMarker = true;
  }

  return pgn;
}

function parseHeaders(pgn: string): Record<string, string> {
  const headers: Record<string, string> = {};

  for (const line of pgn.split(/\r?\n/)) {
    const match = HEADER_TAG_PATTERN.exec(line);
    if (!match) continue;
    const key = match[1];
    const value = match[2];
    if (key === undefined || value === undefined) continue;
    headers[key] = unescapeHeaderValue(value);
  }

  return headers;
}

function findHeader(headers: Record<string, string>, name: string): string | undefined {
  const direct = headers[name];
  if (direct !== undefined) return direct;
  const target = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === target) return headers[key];
  }
  return undefined;
}

function detectResultMarker(pgn: string): string | undefined {
  for (const line of pgn.split(/\r?\n/)) {
    if (line.trim() === "" || isHeaderLine(line)) continue;
    const match = RESULT_MARKER_PATTERN.exec(line.trim());
    const marker = match?.[1];
    if (marker !== undefined && VALID_RESULTS.has(marker)) return marker;
  }
  return undefined;
}

export function gameFromSan(
  moves: string[],
  headers: Record<string, string> = {},
): ParsedGame {
  const headerBlock = Object.entries(headers)
    .map(([key, value]) => `[${key} "${value.replace(/"/g, '\\"')}"]`)
    .join("\n");
  const moveText: string[] = [];
  moves.forEach((san, index) => {
    if (index % 2 === 0) moveText.push(`${index / 2 + 1}.`);
    moveText.push(san);
  });
  const pgn = `${headerBlock}${headerBlock ? "\n\n" : ""}${moveText.join(" ")} *`;
  return parsePgn(pgn);
}

export function parsePgn(pgn: string): ParsedGame {
  const firstGame = extractFirstGame(pgn);
  const chess = new Chess();

  try {
    chess.loadPgn(firstGame);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to parse PGN: ${message}`);
  }

  const headers = parseHeaders(firstGame);
  const moves: ParsedMove[] = chess.history({ verbose: true }).map((move, ply) => ({
    ply,
    san: move.san,
    uci: `${move.from}${move.to}${move.promotion ?? ""}`,
    color: move.color === "w" ? "white" : "black",
    fenBefore: move.before,
    fenAfter: move.after,
    moveNumber: Math.floor(ply / 2) + 1,
  }));

  const headerResult = findHeader(headers, "Result");
  const result =
    headerResult !== undefined && VALID_RESULTS.has(headerResult)
      ? headerResult
      : detectResultMarker(firstGame) ?? "*";

  const opening = findHeader(headers, "Opening");
  const eco = findHeader(headers, "ECO");

  const game: ParsedGame = {
    headers,
    moves,
    result,
    pgn,
  };

  if (opening !== undefined) game.opening = opening;
  if (eco !== undefined) game.eco = eco;

  return game;
}
